/**
 * How the app caches what the server tells it.
 *
 * The defaults here are chosen for a till on a shop's local network, which is
 * a different problem from a web page on the internet.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Data stays fresh for half a minute.
       *
       * The menu does not change while someone is mid-order, and refetching it
       * on every screen change makes a till feel slow for no benefit.
       */
      staleTime: 30_000,

      /**
       * Kept for an hour after nothing is using it, so moving between screens
       * shows the previous answer immediately instead of a spinner.
       */
      gcTime: 60 * 60_000,

      /**
       * Do not retry things that will not get better.
       *
       * A rejected session or a rejected form is an answer, not a failure —
       * retrying it three times just delays telling the person.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isAuth || error.isValidation)) return false;
        return failureCount < 2;
      },

      /**
       * Refetch when the window is focused again.
       *
       * On a till this is what makes the kitchen screen and the counter agree
       * after someone has been looking at another tab.
       */
      refetchOnWindowFocus: true,

      /** And when the branch machine comes back. */
      refetchOnReconnect: true,
    },

    mutations: {
      // Sending an order twice is far worse than telling someone it failed.
      // Anything that must survive a flaky moment goes through the outbox on
      // the branch machine, not through a retry here.
      retry: false,
    },
  },
});
